import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Upload } from "lucide-react";
import { useMemo } from "react";

import { type ColumnDef, type ResourceConfig, ResourcePage } from "@/components/resource-page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "@/services/api";
import { listCursor } from "@/utils/geo";
import { downloadText } from "@/utils/download";
import type { Translate } from "@/constants/i18n";
import { useTranslation } from "@/store/locale";

interface LogEntry {
  id: number;
  employee_no: string;
  full_name: string;
  changes: {
    before: Record<string, string | null>;
    after: Record<string, string | null>;
  } | null;
  system_user: string;
  system_user_id: number;
  handle_user: string;
  handle_user_id: number;
  reason: string;
  status: "done" | "pending" | "rejected";
  created_at: string;
}

interface Options {
  status: { options: { value: string; label: string }[] };
  reason: { options: { value: string; label: string }[] };
}

const options = (signal?: AbortSignal) =>
  api
    .get<Options>("/employee-activity-logs/options", signal ? { signal } : {})
    .then((r) => r.data);
const label = (kind: keyof Options) => async () => (await options())[kind].options;

const TONE: Record<LogEntry["status"], "success" | "elevated" | "danger"> = {
  done: "success",
  pending: "elevated",
  rejected: "danger",
};

/** Exported so the toolbar contract can be asserted without rendering a list. */
export const activityLogConfig = (t: Translate): ResourceConfig<LogEntry> => ({
  title: t("page.activityLog"),
  singular: t("log.entry"),
  endpoint: "/employee-activity-logs",
  backTo: "/employees",
  readOnly: true,
  noView: true,
  // Every event ever raised: it scrolls back through time, and nobody counts it.
  infinite: true,
  searchPlaceholder: t("log.employeeSearch"),
  columns: [
    // Employee number and name as columns of their own, so each sorts, hides
    // and exports on its own rather than being one cell read two ways.
    {
      key: "employee_no",
      label: t("log.employeeNo"),
      fields: ["employee_no"],
      render: (r) => <span className="text-fg tabular-nums">{r.employee_no}</span>,
    },
    {
      key: "full_name",
      label: t("log.name"),
      fields: ["full_name"],
      render: (r) => <span className="text-fg font-medium">{r.full_name}</span>,
    },
    // The two moves the log exists to answer for — where they worked, and
    // what they could do — each shown as the before and after it recorded.
    ...movement(t, "club", "log.clubFrom", "log.clubTo"),
    ...movement(t, "role", "log.roleFrom", "log.roleTo"),
    {
      key: "reason",
      label: t("log.reason"),
      fields: ["reason"],
      // The reason, and nothing else. Naming the changed fields here as well
      // crowded out the one thing the column is for — and Type of Modification
      // beside it already lists every one of them, against its own before and
      // after.
      render: (r, lookup) => <span className="text-fg">{lookup("reason", r.reason)}</span>,
    },
    // Its own column rather than a badge under the reason: it is one of the
    // things the table is read down — every entry that is still pending, say —
    // and a value tucked under another sorts, hides and exports with it.
    {
      key: "status",
      label: t("log.status"),
      fields: ["status"],
      render: (r, lookup) => <Badge tone={TONE[r.status]}>{lookup("status", r.status)}</Badge>,
      csv: (r) => r.status,
    },
    {
      key: "created_at",
      label: t("log.date"),
      sortKey: "created_at",
      fields: ["created_at"],
      render: (r) => (
        <span className="text-fg whitespace-nowrap">
          {format(new Date(r.created_at), "MMM d, yyyy, h:mm a")}
        </span>
      ),
    },
    // Who made the change, not who it was made to — the employee it concerns is
    // already named two columns along, and `system_user` is that same person
    // under another heading. The two differ on almost every row here: an
    // administrator acting on somebody else's account is most of this table.
    {
      key: "handle_user",
      label: t("log.createdBy"),
      fields: ["handle_user"],
      render: (r) => <span className="text-fg">{r.handle_user}</span>,
    },
  ],
  filters: [
    { name: "reason", label: t("log.reason"), type: "select", loadOptions: label("reason") },
    { name: "status", label: t("log.status"), type: "select", loadOptions: label("status") },
    {
      name: "system_user_id",
      label: t("log.employee"),
      type: "fk",
      endpoint: "/users/compact",
    },
    {
      name: "handle_user_id",
      label: t("log.handledBy"),
      type: "fk",
      endpoint: "/users/compact",
    },
    // A window reads only the months it covers; the table holds every event ever raised.
    { name: "date_from", label: t("log.fromDate"), type: "date" },
    { name: "date_to", label: t("log.toDate"), type: "date" },
  ],
  fields: [],
  toForm: () => ({}),
  // Same shape as the Employees list it is opened from: a small search and a
  // funnel on the left, the screen's own actions on the right.
  toolbarVariant: "compact",
  // Every column here is one the table is read for; there is nothing to hide,
  // so the picker would open on a list of columns nobody turns off.
  columnPicker: false,
  // `menuExtra` rather than `toolbar`, which is the slot *before* Refresh:
  // the row reads Refresh then Export, as the Employees row reads Refresh
  // then Archive.
  menuExtra: <ExportButton />,
});

/**
 * A named before/after pair for one field of the change set.
 *
 * Club and role get columns of their own because they are the two questions
 * the log is opened to answer; everything else stays in the generic
 * Modification / From / To trio beside them. A posting is a club change too,
 * so a move into or out of one reads in the Club columns rather than
 * disappearing into the generic block.
 */
const movement = (
  t: Translate,
  key: "club" | "role",
  from: "log.clubFrom" | "log.roleFrom",
  to: "log.clubTo" | "log.roleTo",
): ColumnDef<LogEntry>[] => {
  const read = (entry: LogEntry, side: "before" | "after") => {
    const changes = entry.changes?.[side];
    if (!changes) return null;
    if (key === "role") return changes.role ?? null;
    return changes.temporary_club ?? changes.club ?? null;
  };
  const cell = (side: "before" | "after") => (entry: LogEntry) => (
    <span className="text-fg">{read(entry, side) ?? "—"}</span>
  );
  return [
    { key: `${key}_from`, label: t(from), fields: ["changes"], render: cell("before") },
    { key: `${key}_to`, label: t(to), fields: ["changes"], render: cell("after") },
  ];
};

/** The current page's rows as a CSV file, built in the browser. */
function ExportButton() {
  const t = useTranslation();
  const download = async () => {
    const page = await listCursor<LogEntry>("/employee-activity-logs", { size: 100 });
    const head = [
      t("log.employeeNo"),
      t("log.name"),
      t("log.clubFrom"),
      t("log.clubTo"),
      t("log.roleFrom"),
      t("log.roleTo"),
      t("log.reason"),
      t("log.date"),
      t("log.status"),
      t("log.createdBy"),
    ];
    const side = (r: LogEntry, which: "before" | "after", key: "club" | "role") =>
      key === "role"
        ? (r.changes?.[which]?.role ?? "")
        : (r.changes?.[which]?.temporary_club ?? r.changes?.[which]?.club ?? "");
    const rows = page.items.map((r) => [
      r.employee_no,
      r.full_name,
      side(r, "before", "club"),
      side(r, "after", "club"),
      side(r, "before", "role"),
      side(r, "after", "role"),
      r.reason,
      r.created_at,
      r.status,
      r.handle_user,
    ]);
    const csv = [head, ...rows]
      .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    downloadText(csv, "activity-log.csv");
  };
  return (
    <Button variant="secondary" onClick={() => void download()}>
      <Upload aria-hidden /> {t("rp.export")}
    </Button>
  );
}

function ActivityLogPage() {
  const t = useTranslation();
  const config = useMemo(() => activityLogConfig(t), [t]);
  // Warm the option labels so the first paint already has words, not codes.
  useQuery({
    queryKey: ["employee-activity-logs", "options"],
    queryFn: ({ signal }) => options(signal),
    staleTime: 300_000,
  });
  return <ResourcePage config={config} />;
}

export default ActivityLogPage;
